import React, { useState, useRef, useEffect, useImperativeHandle } from 'react'
import { Box, Radio, RadioGroup, Flex, Icon, Input, Button, VStack, Text } from '@chakra-ui/react'
import { XCloseIcon, CheckIcon, FileQuestion02Icon } from '../../assets/icons'
import FeedbackModal from '../modals/FeedbackModal'

interface MultipleChoiceCardProps {
  answers?: any[]
  onAnswersChange: (answers: any[]) => void
  training?: boolean
  survey?: boolean
  forceSelectingCorrect?: boolean
  onForceCancel?: () => void
  onForceDone?: () => void
  correct_feedback?: string
  incorrect_feedback?: string
  feedback?: { correct_feedback: string; incorrect_feedback: string }
  onFeedbackChange?: (feedback: { correct_feedback: string; incorrect_feedback: string }) => void
  quizMode?: boolean
  quizSubmitted?: boolean
  questionId?: string
  onMCQSelect?: (questionId: string, selectedIndex: number, isCorrect: boolean) => void
  handleFeedbackDisplay?: (result: boolean) => void
  readOnly?: boolean
  userAnswer?: string
  isIndividualResponse?: boolean
  mcqSelectedIndex?: number | null
}

const MultipleChoiceCard = React.forwardRef<
  {
    doneSelectCorrect: () => void
    cancelSelectCorrect: () => void
  },
  MultipleChoiceCardProps
>(
  (
    {
      answers = [],
      onAnswersChange,
      training = false,
      survey = false,
      forceSelectingCorrect,
      quizMode,
      quizSubmitted,
      questionId,
      onMCQSelect,
      feedback = { correct_feedback: '', incorrect_feedback: '' },
      onFeedbackChange,
      handleFeedbackDisplay,
      readOnly = false,
      userAnswer,
      isIndividualResponse = false,
      mcqSelectedIndex = null,
    },
    ref,
  ) => {
    const initOptions = () => {
      let opts
      if (training) {
        opts =
          Array.isArray(answers) && answers.length > 0 && typeof answers[0] === 'object'
            ? answers.slice()
            : [{ text: 'Option 1', correct: false }]
      } else {
        opts = Array.isArray(answers) && answers.length > 0 ? answers.slice() : ['Option 1']
      }
      if (
        opts.length === 0 ||
        (training ? opts[opts.length - 1].text.trim() !== '' : opts[opts.length - 1].trim() !== '')
      ) {
        opts.push(training ? { text: '', correct: false } : '')
      }
      return opts
    }

    const [options, setOptions] = useState<any[]>(initOptions)
    const inputRefs = useRef<(HTMLInputElement | null)[]>([])

    const [error, setError] = useState<string>('')
  // removed unused selectingCorrect state
    const [tempCorrectIndex, setTempCorrectIndex] = useState<number | null>(null)
    const [quizSelectedIndex, setQuizSelectedIndex] = useState<number | null>(mcqSelectedIndex) // Initialize from props
    const isSelectingCorrect = forceSelectingCorrect && !quizMode
    const [validationError, setValidationError] = useState<string>('')
  // removed unused attemptedSave state
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [localFeedback, setLocalFeedback] = useState(feedback)

    useEffect(() => {
      if (!quizMode) {
        setQuizSelectedIndex(null)
      }
    }, [quizMode])

    useEffect(() => {
      // Hydrate from parent-provided selection on any change (including clearing to null)
      setQuizSelectedIndex(mcqSelectedIndex ?? null)
    }, [mcqSelectedIndex])

    // Removed unused matching logic to address lint warnings

    const handleFeedbackChange = (newFeedback: { correct_feedback: string; incorrect_feedback: string }) => {
      setLocalFeedback(newFeedback)
      if (onFeedbackChange) {
        onFeedbackChange(newFeedback)
      }
    }
    const handleOpenModal = () => {
      setIsModalOpen(true)
    }

    const updateAnswersAndValidate = (newOpts: any[]) => {
      const filteredOptions = newOpts.filter((opt, idx) =>
        training
          ? idx !== newOpts.length - 1 || opt.text.trim() !== ''
          : idx !== newOpts.length - 1 || opt.trim() !== '',
      )
      onAnswersChange(filteredOptions)
      if (training) {
        const hasCorrect = newOpts.some((opt) => opt.correct === true)
        setValidationError(hasCorrect ? '' : 'Please select at least one correct answer.')
      }
    }

    useEffect(() => {
      updateAnswersAndValidate(options)
    }, [options, training])

    useEffect(() => {
      if (training) {
        const hasCorrect = options.some((opt) => opt.correct === true)
        setValidationError(hasCorrect ? '' : 'Please select at least one correct answer.')
      } else {
        setValidationError('')
      }
    }, [options, training])

    useEffect(() => {})

    const handleOptionChange = (index: number, value: string) => {
      let newOpts = options.map((opt, i) => {
        if (i === index) {
          return training ? { ...opt, text: value } : value
        }
        return opt
      })

      if (index === options.length - 1) {
        if (training ? value.trim() !== '' : value.trim() !== '') {
          newOpts = [...newOpts, training ? { text: '', correct: false } : '']
        }
      }

      setOptions(newOpts)
    }

    const handleOptionBlur = (index: number, value: string) => {
      if (value.trim() === '') {
        setError('Option cannot be empty.')
        return
      }

      const otherOptions = options.filter((_, i) => i !== index)
      if (otherOptions.some((opt) => (training ? opt.text === value.trim() : opt === value.trim()))) {
        setError('Duplicate options are not allowed.')
        return
      }

      setError('')
    }

    const removeOption = (index: number) => {
      let updated = options.filter((_, i) => i !== index)
      if (training) {
        setTempCorrectIndex(null)
      }
      if (
        updated.length === 0 ||
        (training ? updated[updated.length - 1].text.trim() !== '' : updated[updated.length - 1].trim() !== '')
      ) {
        updated.push(training ? { text: '', correct: false } : '')
      }
      setOptions(updated)
      updateAnswersAndValidate(updated)
    }

    const doneSelectCorrect = () => {
      if (tempCorrectIndex !== null) {
        const updated = options.map((opt, i) => ({ ...opt, correct: i === tempCorrectIndex }))
        setOptions(updated)
        updateAnswersAndValidate(updated)
      }
      setTempCorrectIndex(null)
    }

    const cancelSelectCorrect = () => {
      setTempCorrectIndex(null)
    }

    useImperativeHandle(ref, () => ({
      doneSelectCorrect,
      cancelSelectCorrect,
    }))

    const handleIsQuestionCorrect = (result: boolean) => {
      handleFeedbackDisplay?.(result)
    }

    const handleOptionSelect = (index: number) => {
      if (quizMode && !quizSubmitted) {
        setQuizSelectedIndex(index)
        const isCorrect = training ? !!options[index]?.correct : false
        onMCQSelect && onMCQSelect(questionId ?? '', index, isCorrect)
      } else if (training && isSelectingCorrect) {
        setTempCorrectIndex(index)
      } else if (survey) {
        setQuizSelectedIndex(index)
        // Propagate selection for survey to parent so it gets captured and submitted
        onMCQSelect && onMCQSelect(questionId ?? '', index, false)
      }
    }

    return (
      <>
        <Box className="multiple-choice-card" paddingLeft="20px">
          <RadioGroup>
            <VStack direction="column" w="100%" align="flex-start" spacing="8px">
              {options.map((option, index) => {
                const isLastOption = index === options.length - 1
                if ((isSelectingCorrect || quizMode) && isLastOption) {
                  return null
                }

                if (quizMode && quizSubmitted) {
                  const optionCorrect = training ? !!option.correct : false
                  const optionSelected = quizSelectedIndex === index

                  if (optionSelected) {
                    handleIsQuestionCorrect(optionCorrect && optionSelected)
                  }
                }

                const optionText = training ? option.text : option

                let isSelectedByUser = false

                if (typeof userAnswer === 'string' && typeof optionText === 'string') {
                  isSelectedByUser = userAnswer === optionText

                  if (!isSelectedByUser) {
                    isSelectedByUser = userAnswer.trim().toLowerCase() === optionText.trim().toLowerCase()
                  }

                  if (!isSelectedByUser && !training) {
                    const normalizedUserAnswer = userAnswer.toLowerCase().replace(/\s+/g, '')
                    const normalizedOptionText = optionText.toLowerCase().replace(/\s+/g, '')
                    isSelectedByUser = normalizedUserAnswer === normalizedOptionText

                    if (!isSelectedByUser && normalizedUserAnswer.length > 3) {
                      isSelectedByUser =
                        normalizedOptionText.includes(normalizedUserAnswer) ||
                        normalizedUserAnswer.includes(normalizedOptionText)
                    }
                  }
                }

                let isChecked = false
                let variantToUse = training ? 'success' : 'blackAlpha'

                if (isIndividualResponse) {
                  if (training) {
                    if (option.correct) {
                      isChecked = true
                      variantToUse = 'success'
                    }
                    if (isSelectedByUser && !option.correct) {
                      isChecked = true
                      variantToUse = 'error'
                    }
                  } else {
                    if (isSelectedByUser) {
                      isChecked = true
                      variantToUse = 'primary'
                    }
                  }
                } else if (quizMode && quizSubmitted) {
                  if (training) {
                    if (quizSelectedIndex === index) {
                      isChecked = true
                      variantToUse = option.correct ? 'success' : 'error'
                    } else if (option.correct) {
                      isChecked = true
                      variantToUse = 'success'
                    } else {
                      isChecked = false
                    }
                  } else {
                    isChecked = quizSelectedIndex === index
                  }
                } else if (quizMode) {
                  isChecked = quizSelectedIndex === index
                } else if (training && isSelectingCorrect) {
                  isChecked = tempCorrectIndex === index
                  variantToUse = 'success'
                } else if (training) {
                  isChecked = !!option.correct
                  variantToUse = 'success'
                } else if (survey) {
                  isChecked = quizSelectedIndex === index
                  variantToUse = 'success'
                }

                // removed unused isSurveyChecked

                return (
                  <Flex
                    key={index}
                    direction="row"
                    w="100%"
                    justifyContent="space-between"
                    align="center"
                    bg={training && isSelectingCorrect && tempCorrectIndex === index ? 'success.25' : 'transparent'}
                    h="40px"
                    px="10px"
                    borderRadius="10px"
                  >
                    <Flex w="100%" align="flex-start">
                      <Radio
                        isDisabled={
                          (training && !quizMode && !isSelectingCorrect && !isIndividualResponse) ||
                          (!isSelectingCorrect && readOnly)
                        }
                        variant={variantToUse}
                        isChecked={isChecked || (survey && isSelectedByUser)}
                        onChange={() => handleOptionSelect(index)}
                        size="lg"
                        mt="1px"
                        mr="10px"
                        cursor={readOnly ? 'default' : 'pointer'}
                        pointerEvents={isIndividualResponse ? 'none' : 'auto'}
                      />
                      <Input
                        whiteSpace="pre-wrap"
                        ref={(el) => (inputRefs.current[index] = el)}
                        value={training ? option.text : option}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        onBlur={(e) => handleOptionBlur(index, e.target.value)}
                        variant="multipleChoice"
                        onFocus={(e) => e.target.select()}
                        isReadOnly={quizMode}
                        bg="transparent"
                        color="White.Muted[600]"
                        size="xs"
                        placeholder={index === options.length - 1 ? 'Add option' : ''}
                      />
                    </Flex>
                    {!quizMode && !isSelectingCorrect && !isIndividualResponse && (
                      <Button variant="icon" onClick={() => removeOption(index)}>
                        <Icon boxSize="24px" color="solid.500">
                          <XCloseIcon />
                        </Icon>
                      </Button>
                    )}
                    {training && isSelectingCorrect && tempCorrectIndex === index && (
                      <Icon boxSize="16px" color="success.100">
                        <CheckIcon />
                      </Icon>
                    )}
                  </Flex>
                )
              })}
              {training && !quizMode && !isSelectingCorrect && !isIndividualResponse && (
                <Box
                  as="button"
                  px="8px"
                  h="40px"
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="flex-start"
                  color="primary.100"
                  onClick={handleOpenModal}
                  sx={{
                    _focus: { boxShadow: 'none', outline: 'none' },
                  }}
                  _hover={{ borderColor: 'transparent' }}
                >
                  <Icon boxSize="24px" mr="15px">
                    <FileQuestion02Icon />
                  </Icon>
                  <Text variant="h-xs">Add Answer Feedback</Text>
                </Box>
              )}

              {error && (
                <Text color="red.500" fontSize="sm">
                  {error}
                </Text>
              )}
            </VStack>
          </RadioGroup>

          {validationError && (
            <Text color="red.500" fontSize="sm">
              {validationError}
            </Text>
          )}
        </Box>
        {isModalOpen && (
          <FeedbackModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            feedback={localFeedback}
            onFeedbackChange={handleFeedbackChange}
          />
        )}
      </>
    )
  },
)

export default MultipleChoiceCard
